<template>
    <modal
        class="component-modal"
        :title="title"
        header-bg-variant="blue-600"
        header-text-variant="white"
        :hide-footer="isLoading"
    >
        <div v-if="!isLoading" class="flex-form" @click="closeDropdown">
            <section class="flex-form__section">
                <div class="flex-form__field">
                    <div class="flex-form__label">Component Name</div>

                    <input
                        ref="componentNameInput"
                        class="flex-form__input"
                        :class="{ 'is-invalid': !validName || !uniqueName }"
                        type="text"
                        :value="componentName"
                        @input="onNameInput"
                    >
                </div>

                <div class="flex-form__fieldset">
                    <div class="flex-form__field">
                        <div class="flex-form__label">Category</div>

                        <div>
                            <dropdown
                                class="flex-form__dropdown"
                                :class="{ 'is-invalid': !validCategory }"
                                placeholder="Select a Category"
                                :options="categoryOptions"
                                :value="category"
                                @input="onCategoryInput"
                            />

                            <input
                                v-if="addingCategory"
                                :id="categoryNewId"
                                class="flex-form__input flex-form__input--inline"
                                :class="{ 'is-invalid': !validCategoryCustom }"
                                type="text"
                                :value="categoryCustom"
                                @input="onCategoryCustomInput"
                            >

                            <button
                                class="flex-form__add flex-form__add--inline"
                                type="button"
                                @click="addingCategory = !addingCategory"
                            >
                                <font-awesome-icon :icon="['fal', addCategoryIcon]" />
                            </button>
                        </div>
                    </div>

                    <div v-if="category" class="flex-form__field">
                        <div class="flex-form__label">Subcategory</div>

                        <div>
                            <dropdown
                                class="flex-form__dropdown"
                                :class="{ 'is-invalid': !validSubcategory }"
                                placeholder="Select a Subcategory"
                                :options="subcategoryOptions"
                                :value="subcategory"
                                @input="onSubcategoryInput"
                            />

                            <input
                                v-if="addingSubcategory"
                                :id="subcategoryNewId"
                                class="flex-form__input flex-form__input--inline"
                                :class="{ 'is-invalid': !validSubcategoryCustom }"
                                type="text"
                                :value="subcategoryCustom"
                                @input="onSubcategoryCustomInput"
                            >

                            <button
                                class="flex-form__add flex-form__add--inline"
                                type="button"
                                @click="addingSubcategory = !addingSubcategory"
                            >
                                <font-awesome-icon :icon="['fal', addSubcategoryIcon]" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section id="shared-content" class="flex-form__section">
                <div class="flex-form__toggle">
                    <div class="flex-form__toggle-control">
                        <toggle v-model="isSharedContent" :messages="toggleMessages" />
                    </div>

                    <div class="flex-form__toggle-text">
                        Save as Global Content

                        <span v-tooltip="sharedContentTooltip" class="flex-form__tooltip">
                            <font-awesome-icon :icon="['fal', 'question-circle']" />
                        </span>
                    </div>
                </div>
            </section>
        </div>

        <loading-icon
            v-if="isLoading"
            type="spinner"
            class="component-modal__loading"
            :color="loadingIconColor"
        />

        <template v-slot:modal-footer>
            <span class="component-modal__error">
                {{ errorMessage }}
            </span>

            <button class="component-modal__button component-modal__button--cancel" @click="hideModal">
                Cancel
            </button>

            <button class="component-modal__button component-modal__button--continue" @click="onSave">
                Save
            </button>
        </template>
    </modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import uuidv4 from 'uuid/v4';

import LoadingIcon from '@/components/shared/loading/loading-icon';
import { CREATE_COMPONENT, LOAD_COMPONENT_TREE, LOAD_PAGE } from '@/store/types/action-types';
import { COMPONENT_TREE_ITEM_NAMES, GET_COMPONENT_TREE_ITEM } from '@/store/types/getter-types';
import {
    CLEAR_COPIED_MENU_DATA,
    SET_SELECTED_COMPONENT,
    SET_SELECTED_COMPONENT_METADATA,
    TRIGGER_PAGE_CREATED,
    HIDE_MODAL,
    CLEAR_COMPONENT_DATA,
} from '@/store/types/mutation-types';
import { fetchRendering, generateRendering } from '@/utils/rendering';
import mapFilters from '@/utils/map-filters';
import { COMPONENT_TREE_SECTIONS } from '@/data/constants/component-tree-sections';
import appThemes from '@/data/app-themes';

const COPY_MODE = 'copy';
const SAVE_MODE = 'save';
const EMPTY_DROPDOWN_VALUE = '';

export default {
    components: { LoadingIcon },

    data() {
        return {
            categoryNewId: 'component-category-new',
            subcategoryNewId: 'component-subcategory-new',

            componentName: '',
            errorMessage: '',

            addingCategory: false,
            category: '',
            categoryCustom: '',

            addingSubcategory: false,
            subcategory: '',
            subcategoryCustom: '',

            isSharedContent: false,
            isLoading: false,

            sharedContentTooltip: {
                container: '#shared-content',
                content: 'Global Content can only be modified inside the Component Editor.',
                class: 'v-popper__popper--component-save',
            },

            toggleMessages: {
                checked: 'Yes',
                unchecked: 'No',
            },

            touched: {
                name: false,
                category: false,
                categoryCustom: false,
                subcategory: false,
                subcategoryCustom: false,
            },
        };
    },

    computed: {
        ...mapGetters({
            componentTreeItemNames: COMPONENT_TREE_ITEM_NAMES,
            getComponentTreeItem: GET_COMPONENT_TREE_ITEM,
        }),

        ...mapState({
            appTheme: state => state.page.appTheme,
            componentTree: state => state.page.componentTree,
            copiedMenuData: state => state.page.copiedMenuData,
            pageData: state => state.page.pageData,
            selectedComponent: state => state.page.selectedComponent,
            selectedComponentMetadata: state => state.page.selectedComponentMetadata,
        }),

        mode() {
            return this.copiedMenuData ? COPY_MODE : SAVE_MODE;
        },

        title() {
            return this.mode === COPY_MODE ? 'Copy Component' : 'Save Component';
        },

        section() {
            return this.isSharedContent ? COMPONENT_TREE_SECTIONS.SHARED : COMPONENT_TREE_SECTIONS.CLIENT;
        },

        componentData() {
            return {
                componentCategory: this.category || this.categoryCustom,
                componentSubcategory: this.subcategory || this.subcategoryCustom,
                componentType: this.isSharedContent ? 'Shared' : 'Global',
                editorName: this.componentName,
                id: this.mode === COPY_MODE ? uuidv4() : this.selectedComponent.id,
            };
        },

        categoryTree() {
            if (!this.componentTree.length) return [];

            const category = this.componentTree.filter(section => section.name === this.section)[0];

            return category ? category.items : [];
        },

        // returns a list of available menu categories (e.g. 'Cards', 'Heroes', etc)
        categoryOptions() {
            if (!this.categoryTree.length) {
                return [{
                    text: 'No categories exist!',
                    value: EMPTY_DROPDOWN_VALUE,
                    disabled: true,
                }];
            }

            return Array.from(this.categoryTree)
                .sort((a, b) => (a.displayName.toLowerCase() < b.displayName.toLowerCase() ? -1 : 1))
                .map(category => ({
                    text: category.displayName,
                    value: category.displayName,
                }));
        },

        subcategoryOptions() {
            if (!this.categoryMenuItem) return [];

            const subcategories = this.categoryMenuItem.children
                .filter(child => !child.props || !child.props.componentItemID) // "subcategories" = category items w/out a componentItemID
                .sort((a, b) => (a.displayName.toLowerCase() < b.displayName.toLowerCase() ? -1 : 1))
                .map(subcategory => ({
                    text: subcategory.displayName,
                    value: subcategory.displayName,
                }));

            if (!subcategories.length) {
                return [{
                    text: 'No categories exist!',
                    value: EMPTY_DROPDOWN_VALUE,
                    disabled: true,
                }];
            }

            subcategories.unshift({
                text: 'None',
                value: EMPTY_DROPDOWN_VALUE,
            });

            return subcategories;
        },

        hasSubcategories() {
            return this.subcategoryOptions.length && this.subcategoryOptions[0].value !== EMPTY_DROPDOWN_VALUE;
        },

        categoryMenuItem() {
            return this.categoryTree.find(category => category.displayName === this.category);
        },

        addCategoryIcon() {
            return this.addingCategory ? 'minus' : 'plus';
        },

        addSubcategoryIcon() {
            return this.addingSubcategory ? 'minus' : 'plus';
        },

        valid() {
            return this.validName
                && this.uniqueName
                && this.validCategory
                && this.validCategoryCustom
                && this.validSubcategory
                && this.validSubcategoryCustom;
        },

        validName() {
            if (!this.touched.name) return true;

            // must not be empty
            if (this.componentName.trim().length < 1) return false;

            return /^[a-zA-Z0-9 ]+$/g.test(this.componentName);
        },

        uniqueName() {
            if (!this.touched.name) return true;

            // must not already exist
            return !this.componentTreeItemNames.includes(this.componentName);
        },

        validCategory() {
            if (this.addingCategory || !this.touched.category) return true;

            // must not be empty
            return !!this.category;
        },

        validCategoryCustom() {
            if (!this.addingCategory || !this.touched.categoryCustom) return true;

            // must not be empty
            return !!this.categoryCustom.replace(/ /g, '');
        },

        validSubcategory() {
            if (!this.hasSubcategories || this.addingSubcategory || !this.touched.subcategory) return true;

            // must not be empty
            return !!this.subcategory;
        },

        validSubcategoryCustom() {
            if (!this.addingSubcategory || !this.touched.subcategoryCustom) return true;

            // must not be empty
            return !!this.subcategoryCustom.replace(/ /g, '');
        },

        loadingIconColor() {
            return appThemes[this.appTheme].app.color;
        },
    },

    watch: {
        async addingCategory(isAdding) {
            if (isAdding) {
                await this.$nextTick();
                // can't use the ref attribute via markup, as the input is part of the slot content
                document.getElementById(this.categoryNewId).focus();
                this.category = '';
            } else {
                this.categoryCustom = '';
            }
        },

        async addingSubcategory(isAdding) {
            if (isAdding) {
                await this.$nextTick();
                // can't use the ref attribute via markup, as the input is part of the slot content
                document.getElementById(this.subcategoryNewId).focus();
                this.subcategory = '';
            } else {
                this.subcategoryCustom = '';
                this.showValidationError();
            }
        },

        componentName() {
            this.showValidationError();
        },

        category(newVal) {
            this.showValidationError();
            this.subcategory = '';

            if (newVal) this.addingCategory = false;
        },

        categoryCustom() {
            this.showValidationError();
        },

        subcategory(newVal) {
            this.showValidationError();

            if (newVal) this.addingSubcategory = false;
        },

        subcategoryCustom() {
            this.showValidationError();
        },

        isSharedContent() {
            this.resetDropdowns();
        },
    },

    async mounted() {
        // timeout needed to wait for the name input to appear, as waiting for the next tick alone won't suffice
        window.setTimeout(() => this.$refs.componentNameInput.focus(), 500);

        if (this.mode === COPY_MODE) {
            this.isSharedContent = this.copiedMenuData.sectionName === COMPONENT_TREE_SECTIONS.SHARED;

            // toggling isSharedContent resets the dropdowns, so we need to wait for the next tick
            await this.$nextTick();
            this.componentName = this.getMenuCopyName(this.copiedMenuData.menuItem.displayName);
            this.category = this.copiedMenuData.categoryName;

            // toggling category resets the subcategory dropdown, so we need to wait for the next tick
            await this.$nextTick();
            this.subcategory = this.copiedMenuData.subcategoryName;
        }
    },

    methods: {
        ...mapFilters(['paramCase']),

        ...mapActions({
            createComponent: CREATE_COMPONENT,
            loadComponentTree: LOAD_COMPONENT_TREE,
            loadPage: LOAD_PAGE,
        }),

        ...mapMutations({
            clearCopiedMenuData: CLEAR_COPIED_MENU_DATA,
            clearComponentData: CLEAR_COMPONENT_DATA,
            hideModal: HIDE_MODAL,
            setSelectedComponent: SET_SELECTED_COMPONENT,
            setSelectedComponentMetadata: SET_SELECTED_COMPONENT_METADATA,
            triggerComponentSaved: TRIGGER_PAGE_CREATED,
        }),

        endCopy() {
            this.$root.$emit('flex::component::copy::end');
            this.clearCopiedMenuData();
        },

        onNameInput(event) {
            this.componentName = event.target.value;
            this.touched.name = true;
        },

        onCategoryInput(value) {
            this.category = value;
            this.touched.category = true;
            this.addingSubcategory = false;
        },

        onCategoryCustomInput(event) {
            this.categoryCustom = event.target.value;
            this.touched.categoryCustom = true;
        },

        onSubcategoryInput(value) {
            this.subcategory = value;
            this.touched.subcategory = true;
        },

        onSubcategoryCustomInput(event) {
            this.subcategoryCustom = event.target.value;
            this.touched.subcategoryCustom = true;
        },

        getMenuCopyName(displayName) {
            const COPY_TEXT = ' Copy';
            const baseText = displayName.includes(COPY_TEXT)
                ? displayName.slice(0, displayName.indexOf(COPY_TEXT))
                : displayName;
            const matchingItems = this.componentTreeItemNames.filter((itemName) => {
                return itemName.includes(baseText) && itemName.includes(COPY_TEXT);
            });
            const hasCopies = matchingItems.length;

            let copyNumber = 0;

            if (hasCopies) {
                // increment the highest copy number
                const highestCopyNumber = matchingItems.reduce((highestNumber, itemName) => {
                    const number = Number(itemName.slice(itemName.indexOf(COPY_TEXT) + COPY_TEXT.length));

                    return number > highestNumber ? number : highestNumber;
                }, copyNumber);

                // the first copy should display "- Copy", the second should display "- Copy 2", the third should display "- Copy 3", etc etc
                copyNumber = highestCopyNumber === 0 ? 2 : highestCopyNumber + 1;
            }

            // if the copy number is zero - conver to an empty string
            // otherwise - pad it with a space at the beginning
            copyNumber = copyNumber ? ` ${ copyNumber }` : '';

            return `${ baseText }${ COPY_TEXT }${ copyNumber }`;
        },

        async onSave() {
            Object.keys(this.touched).forEach((fieldKey) => {
                if (fieldKey !== 'subcategory') this.touched[fieldKey] = true;
            });

            if (!this.valid) {
                this.showValidationError();
                return;
            }

            this.isLoading = true;

            if (this.mode === SAVE_MODE) {
                this.selectedComponent.lockComponent = true;

                if (this.isSharedContent) this.selectedComponent.globalContent = true;
            }

            const component = await this.createComponent({
                component: this.mode === COPY_MODE
                    ? await generateRendering(this.copiedMenuData.menuItem)
                    : this.selectedComponent,
                componentData: this.componentData,
            });

            if (component) {
                this.onSaveComplete(component.data);
            }
        },

        async onSaveComplete(componentId) {
            this.clearComponentData();

            // reload tree to get new data
            await this.loadComponentTree();

            if (this.mode === COPY_MODE) {
                this.endCopy();
                this.$router.push({ path: `/components/${ componentId }` });
            }

            if (this.mode === SAVE_MODE) {
                this.setSelectedComponentMetadata({
                    editorName: this.componentName,
                    componentType: this.componentData.componentType,
                });

                this.reloadComponent(componentId); // needed in order to cache the ID generated by Sitecore
                this.$router.push({ path: `/components/${ componentId }` });
            }

            this.openToolbarMenu();
            this.hideModal();
        },

        openToolbarMenu() {
            const sectionId = this.paramCase(this.section);
            const categoryId = this.paramCase(this.componentData.componentCategory);
            const subcategoryId = this.componentData.componentSubcategory ? this.paramCase(this.componentData.componentSubcategory) : '';
            const MENU_TRANSITION_DELAY = 500;

            this.$root.$emit('flex::accordion::show', sectionId);

            window.setTimeout(() => {
                this.$root.$emit('flex::toolbar::show', {
                    section: sectionId,
                    id: categoryId,
                });
            }, MENU_TRANSITION_DELAY);

            if (subcategoryId) {
                window.setTimeout(() => {
                    this.$root.$emit('flex::toolbar::show', {
                        section: sectionId,
                        category: categoryId,
                        id: subcategoryId,
                    });
                }, MENU_TRANSITION_DELAY * 2);
            }
        },

        showValidationError() {
            switch (true) {
                case !this.validName:
                    this.errorMessage = 'Please enter a valid name. Only alphanumeric characters are allowed.';
                    break;

                case !this.uniqueName:
                    this.errorMessage = 'That component name is already taken.';
                    break;

                case !this.validCategory:
                    this.errorMessage = 'Please select a category, or add a new one.';
                    break;

                case !this.validCategoryCustom:
                    this.errorMessage = 'Please enter a category name.';
                    break;

                case !this.validSubcategory:
                    this.errorMessage = 'Please select a subcategory, or add a new one.';
                    break;

                case !this.validSubcategoryCustom:
                    this.errorMessage = 'Please enter a subcategory name.';
                    break;

                default:
                    this.errorMessage = '';
            }
        },

        async reloadComponent(componentItemID) {
            const rendering = await fetchRendering(componentItemID);

            this.setSelectedComponentMetadata({
                editorName: rendering.editorName,
                id: rendering.id,
                componentType: rendering.globalContent ? COMPONENT_TREE_SECTIONS.SHARED : COMPONENT_TREE_SECTIONS.CLIENT,
            });

            this.setSelectedComponent(rendering);
        },

        closeDropdown() {
            this.$root.$emit('flex::dropdown::close');
        },

        resetDropdowns() {
            this.errorMessage = '';

            this.addingCategory = false;
            this.category = '';
            this.categoryCustom = '';

            this.addingSubcategory = false;
            this.subcategory = '';
            this.subcategoryCustom = '';

            Object.keys(this.touched).forEach((fieldKey) => {
                this.touched[fieldKey] = false;
            });
        },
    },
};
</script>

<style lang="scss">
.component-modal {
    h1, h2, h3, h4, h5, h6 {
        font-family: $default-font-family;
    }

    .modal-title {
        font-size: 18px;
    }

    .modal-content {
        min-width: 846px;
        min-height: 379px;
    }

    &__loading {
        @extend %center-middle;
    }

    &__error {
        color: $attention-color;
        position: absolute;
        left: 20px;
        font-size: 14px;
    }

    &__button {
        border: 1px solid;
        width: 100px;
        height: 35px;

        &--continue {
            color: var(--form-buttonColor);
            background-color: var(--form-buttonBackgroundColor);
            border: 1px solid var(--form-buttonBorderColor);

            &:hover {
                color: var(--form-buttonHoverColor);
                background-color: var(--form-buttonHoverBackgroundColor);
            }
        }

        &--cancel {
            color: var(--form-altButtonColor);
            background-color: var(--form-altButtonBackgroundColor);
            border: 1px solid var(--form-altButtonBorderColor);

            &:hover {
                color: var(--form-altButtonHoverColor);
                background-color: var(--form-altButtonHoverBackgroundColor);
            }
        }
    }
}

.v-popper__popper--component-save {
    font-size: 14px;
}
</style>
